import { EVENTS, IRentalProperties, pickerElement, RentalDatePicker, trigger } from "./DateRangePicker";
import { Dropdown } from "../../../Components/Form/Dropdown";
import Configurator from "../../Product/RentalConfigurator";
import { pickedRange } from "../Helper/DateRangePicker";
import { DateTime } from "@easepick/bundle";
import { Variant } from "../../../Interfaces/Product";

const SELECTORS_EBIKE = {
    sizeInput: ':scope x-dropdown[name="option1"]',
    priceList: ":scope .rental-information > .product-additional.price-list"
};

interface IRentalEbikeProperties extends IRentalProperties {
    _size?: string;
    _location?: string;
}

class RentalDatePickerEbike extends RentalDatePicker {
    sizeInput: Dropdown;
    priceList: HTMLElement;
    pageLoad: boolean;
    change: () => void;

    /**
     * initialize event listeners
     */
    initListener() {
        super.initListener();
        this.select = this.selectHandler.bind(this);
        this.change = this.changeHandler.bind(this);
        // This is for preventing datepicker to be cleared on pageload
        window.addEventListener("load", () => {
            this.pageLoad = true;
        });
        return this;
    }

    constructor() {
        super();
        // Added this to here to not extend init function
        this.sizeInput = this.detailsWrapper.querySelector(SELECTORS_EBIKE.sizeInput);
        this.priceList = document.querySelector(SELECTORS_EBIKE.priceList);
    }

    async init() {
        await super.init();

        const selectedVariant = Configurator.selectedVariant;
        if (selectedVariant && pickedRange.start && pickedRange.end) {
            const rangeStart = new DateTime(pickedRange.start);
            const rangeEnd = new DateTime(pickedRange.end);
            if (this.isBookedVariant(selectedVariant, rangeStart, rangeEnd)) {
                this.picker.clear();
            }
        }

        this.sizeInput.value && this.sizeInput.dispatchEvent(new Event("change"));
    }

    addPickerListeners() {
        super.addPickerListeners();
        this.sizeInput.addEventListener("change", this.changeHandler.bind(this));

        return this;
    }

    changeHandler() {
        this.bookedDates = [];
        if (!this.isVariantAvailable) {
            trigger.classList.add("disabled");
            trigger.removeEventListener(EVENTS.click, this.select);
            return;
        } else {
            trigger.classList.remove("disabled");
            trigger.addEventListener(EVENTS.click, this.select);
            const bookingRangesNew = this.bookingRanges.filter((b) => {
                const selectedVariant = this.variants.find((v) => +v.id === b.variant_id);

                return selectedVariant.size === this.selectedSize;
            });
            bookingRangesNew.forEach((b) => {
                let next = b.start.clone().add(1);
                const selectedVariant = this.variants.find((v) => +v.id === b.variant_id);
                if (selectedVariant.size === this.selectedSize) {
                    this.bookedDates.push(b.start);
                    while (next.isBefore(b.end)) {
                        this.bookedDates.push(next);
                        next = next.clone().add(1);
                    }
                    this.bookedDates.push(b.end);
                }
            });

            this.picker.setRanges(bookingRangesNew);
            this.variantBookingRanges = bookingRangesNew;
        }
        if (!this.pageLoad) {
            this.picker.clear();
        }
        this.pageLoad = false;
    }

    /**
     * determine if the given bike is booked during given period
     * @private
     */
    private isBookedVariant(variant: Variant, start: DateTime, end: DateTime): boolean {
        return this.bookingRanges
            .filter((r) => r.variant_id === variant.id)
            .some((r) => r.start.isSameOrBefore(end) && r.end.isSameOrAfter(start));
    }

    selectHandler() {
        const startDate = this.picker.getStartDate();
        if (startDate && this.selectedSize) {
            this.updateUiAndProperties();
            const properties: IRentalEbikeProperties = this.properties;
            this.disabledCta = false;
            const duration = this.end.diff(this.start);
            const variant = this.variants.filter((v) => v.size === this.selectedSize && v.days <= duration).pop();
            properties._rental_duration = Math.max(this.end.diff(this.start), 1);
            properties._price_per_day = this.prices
                .filter((v) => v.size === this.selectedSize && v.days <= duration)
                .pop().price;
            properties._tariff = variant.tariff;
            properties._size = variant.size;
            properties._location = this.location;
            this.quantity = properties._rental_duration;
            this.properties = properties;
            this.variant = variant.id;
            this.price = properties._price_per_day * properties._rental_duration;
        }
    }

    get variants() {
        return this.detailsWrapper.dataset.variants.split(",").map((v) => {
            const parts = v.split(":");
            return {
                size: parts[0],
                tariff: parts[1],
                days: parts[1].match(/\d/) ? parseInt(v.split(":")[1].replace(/\D/g, "")) : 0,
                id: parts[2]
            };
        });
    }

    get prices() {
        return this.detailsWrapper.dataset.prices.split(",").map((v) => {
            const parts = v.split(":");
            return {
                size: parts[0],
                tariff: parts[1],
                days: parts[1].match(/\d/) ? parseInt(v.split(":")[1].replace(/\D/g, "")) : 0,
                price: parseInt(parts[2])
            };
        });
    }

    get selectedSize() {
        return this.sizeInput.value;
    }

    get isVariantAvailable() {
        return this.product.some((p) => {
            const option1 = p.options.find((o) => o.position === 1);
            return option1.values.includes(this.selectedSize);
        });
    }

    get location() {
        return this.locations.find((l) => l.size === this.selectedSize).location;
    }

    get locations() {
        return this.detailsWrapper.dataset.locations.split(",").map((v) => {
            const parts = v.split(":");
            return {
                size: parts[0],
                location: parts[1]
            };
        });
    }
}

if (document.body.classList.contains("e-bike-rental") && pickerElement && trigger) {
    new RentalDatePickerEbike();
}
